import { defineStore, storeToRefs } from "pinia";
import { ref } from "vue";
import { useAuthStore } from "./auth";
import { useClientConfig } from "./client-config";
import { trackIdentify } from "@/utils/tracking";
import { setupDemo } from "./demo";
import { useLogger } from "@/services/logger";

export type AppState = "LOADING" | "IDLE" | "ERROR";

const logger = useLogger();

export const useAppStore = defineStore(
    "app",
    () => {
        const appState = ref<AppState>("LOADING");
        const loadingMessage = ref<string>();

        const authStore = useAuthStore();

        const { isAuthenticated } = storeToRefs(authStore);
        const clientConfig = useClientConfig();

        async function onIsAuthenticatedChange() {
            if (!isAuthenticated.value) {
                return;
            }

            appState.value = "LOADING";
            try {
                loadingMessage.value = "Loading";
                await clientConfig.ensureTenants();
                if (clientConfig.selectedTenant === -1) {
                    appState.value = "IDLE";
                    return;
                }
                await clientConfig.fetchConfig();
                loadingMessage.value = "Loading";
                await authStore.fetchActiveUser();
                appState.value = "IDLE";

                trackIdentify({
                    user: authStore.agent!,
                    tenantId: clientConfig.selectedTenant,
                });

                setupDemo();
            } catch (e) {
                appState.value = "ERROR";
                logger.error(e);
            }
        }

        async function start() {
            await onIsAuthenticatedChange();
            if (!isAuthenticated.value) {
                appState.value = 'IDLE';
            }
        }

        return {
            appState,
            loadingMessage,
            start,
        };
    },
);
